import React, { useMemo } from "react";
import { FieldValues } from "react-hook-form";
import {
  EntityController,
  EntityHandlerContext,
  useEntityHandler,
} from "../../controllers/entityController";
import { MainPaper, SectionPaper } from "../common/Papers";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Grid2 as Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import GeneralAlert from "../common/GeneralAlert";
import { singleGridItem } from "../forms/fields/defaultStyles";

interface ViewPageProps<T extends FieldValues> {
  controller: EntityController<T>;
  children: React.ReactNode;
  sections?: (data: T) => React.ReactElement[];
}

/**
 * Generic ViewPage component for displaying entity data in read-only mode.
 * Uses the same useEntityHandler as FormPage - the form methods are simply unused.
 * This simplifies the architecture by having a single handler for all pages.
 */
const ViewPage = <T extends FieldValues>({
  controller,
  children,
  sections,
}: ViewPageProps<T>) => {
  // Use the same handler as FormPage - we just don't use the form-specific methods
  const handler = useEntityHandler(controller);
  const { loading, fetchError, title, methods } = handler;

  const navigate = useNavigate();

  const renderedSections = useMemo(() => {
    if (!sections) return null;
    const values = methods.getValues();
    return sections(values).map((section, index) => (
      <SectionPaper key={index}>{section}</SectionPaper>
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, loading, fetchError]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <GeneralAlert
        errorMessage={fetchError}
        action={() => navigate(0)}
        actionLabel="Retry"
      />
    );
  }

  return (
    <EntityHandlerContext.Provider value={handler}>
      <MainPaper>
        <Box
          sx={{
            mb: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h2">{title}</Typography>
          {controller.getEditRoute && (
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => navigate(controller.getEditRoute!())}
            >
              Edit
            </Button>
          )}
        </Box>
        {children}
      </MainPaper>
      {renderedSections && (
        <Grid container {...singleGridItem}>
          <Grid {...singleGridItem}>{renderedSections}</Grid>
        </Grid>
      )}
    </EntityHandlerContext.Provider>
  );
};

export default ViewPage;
